// Centralised theme tokens for the PDF documents. These mirror the on-screen
// brand palette but are inlined here because @react-pdf/renderer cannot read
// CSS custom properties.
import { StyleSheet } from '@react-pdf/renderer'

export const pdfColors = {
  brandPrimary: '#0A2540',
  brandSecondary: '#1976D2',
  brandAccent1: '#05C3DE',
  brandAccent2: '#3CDBC0',
  textPrimary: '#1A1A1A',
  textSecondary: '#4B5563',
  textTertiary: '#9CA3AF',
  border: '#E5E7EB',
  surface: '#FFFFFF',
  background: '#F9FAFB',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
}

export const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 70,
    paddingBottom: 60,
    paddingHorizontal: 36,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: pdfColors.textPrimary,
  },
  h1: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: pdfColors.brandPrimary,
    marginBottom: 8,
  },
  h2: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: pdfColors.brandPrimary,
    marginTop: 16,
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1.5,
    borderBottomColor: pdfColors.brandSecondary,
  },
  h3: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: pdfColors.brandPrimary,
    marginTop: 10,
    marginBottom: 4,
  },
  paragraph: {
    fontSize: 10,
    lineHeight: 1.5,
    color: pdfColors.textSecondary,
    marginBottom: 6,
  },
  small: {
    fontSize: 8,
    color: pdfColors.textTertiary,
  },
  // KPI grid
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 10,
    gap: 8,
  },
  kpiCard: {
    width: '32%',
    padding: 10,
    backgroundColor: pdfColors.gray50,
    borderLeftWidth: 3,
    borderLeftColor: pdfColors.brandSecondary,
    marginBottom: 6,
  },
  kpiCardLabel: {
    fontSize: 8,
    color: pdfColors.textSecondary,
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  kpiCardValue: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: pdfColors.brandPrimary,
  },
  kpiCardSub: {
    fontSize: 8,
    color: pdfColors.textTertiary,
    marginTop: 2,
  },
  // Table
  table: {
    marginTop: 4,
    borderWidth: 0.5,
    borderColor: pdfColors.border,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: pdfColors.border,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: pdfColors.brandPrimary,
  },
  tableHeaderCell: {
    paddingVertical: 6,
    paddingHorizontal: 6,
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.5,
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  tableCell: {
    paddingVertical: 5,
    paddingHorizontal: 6,
    fontSize: 9,
    color: pdfColors.textPrimary,
  },
  tableRowAlt: {
    backgroundColor: pdfColors.gray50,
  },
  // Bar chart
  barChartItem: {
    marginBottom: 5,
  },
  barChartLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  barChartLabel: {
    fontSize: 9,
    color: pdfColors.textPrimary,
  },
  barChartValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: pdfColors.brandSecondary,
  },
  barTrack: {
    height: 8,
    backgroundColor: pdfColors.gray100,
    borderRadius: 2,
  },
  barFill: {
    height: 8,
    backgroundColor: pdfColors.brandSecondary,
    borderRadius: 2,
  },
})

// Severity → colour mapping for severity badges
export const severityColors: Record<string, string> = {
  low: pdfColors.textTertiary,
  medium: pdfColors.warning,
  high: pdfColors.error,
  critical: '#7F1D1D',
}

// Status → colour mapping for status badges
export const statusColors: Record<string, string> = {
  pending: pdfColors.warning,
  applied: pdfColors.success,
  dismissed: pdfColors.textTertiary,
  expired: pdfColors.textTertiary,
}
